import { toUserFacingApiError } from "lib/utils/userFacingApiError";
/** Fixed copy for the settings “Test” action. */
export const TELEGRAM_TEST_MESSAGE =
    "Greetings, this is a test message from R2C2 ARC platform.";

type TelegramSendResponse = {
    ok?: boolean;
    description?: string;
};

function requireTokenChat(botToken: string, chatId: string): { token: string; chat: string } {
    const token = botToken.trim();
    const chat = chatId.trim();
    if (!token || !chat) {
        throw new Error("Bot token and chat ID are required.");
    }
    return { token, chat };
}

async function handleTelegramFetchError(e: unknown): Promise<never> {
    const msg = e instanceof Error ? e.message : "Network error";
    throw new Error(
        `${msg}. If this persists in the browser, Telegram may be blocking the request (CORS); use a small server proxy to the Bot API.`
    );
}

async function parseTelegramJson(res: Response): Promise<TelegramSendResponse> {
    try {
        return (await res.json()) as TelegramSendResponse;
    } catch {
        throw new Error("Couldn't complete the Telegram request. Try again.");
    }
}

/**
 * POSTs to Telegram Bot API sendMessage.
 * Note: Browsers may block this with CORS unless requests go through your own backend proxy.
 */
export async function sendTelegramMessage(
    botToken: string,
    chatId: string,
    text: string
): Promise<void> {
    const { token, chat } = requireTokenChat(botToken, chatId);

    const url = `https://api.telegram.org/bot${encodeURIComponent(token)}/sendMessage`;
    let res: Response;
    try {
        res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ chat_id: chat, text }),
        });
    } catch (e) {
        return handleTelegramFetchError(e);
    }

    const data = await parseTelegramJson(res);
    if (!res.ok || data.ok !== true) {
        throw new Error(toUserFacingApiError(data.description, "Couldn't complete the Telegram request. Try again."));
    }
}

type TelegramMultipartMethod = "sendPhoto" | "sendVideo" | "sendDocument";

async function postTelegramMultipart(
    token: string,
    method: TelegramMultipartMethod,
    form: FormData
): Promise<void> {
    const url = `https://api.telegram.org/bot${encodeURIComponent(token)}/${method}`;
    let res: Response;
    try {
        res = await fetch(url, {
            method: "POST",
            body: form,
        });
    } catch (e) {
        return handleTelegramFetchError(e);
    }

    const data = await parseTelegramJson(res);
    if (!res.ok || data.ok !== true) {
        throw new Error(toUserFacingApiError(data.description, "Couldn't complete the Telegram request. Try again."));
    }
}

/**
 * Sends an image via sendPhoto (multipart). Prefer JPEG/PNG blobs under Bot API size limits.
 */
export async function sendTelegramPhoto(
    botToken: string,
    chatId: string,
    file: Blob,
    options?: { caption?: string; filename?: string }
): Promise<void> {
    const { token, chat } = requireTokenChat(botToken, chatId);
    const form = new FormData();
    form.append("chat_id", chat);
    const name =
        options?.filename ||
        (file instanceof File ? file.name : null) ||
        (file.type === "image/png" ? "map.png" : "map.jpg");
    form.append("photo", file, name);
    if (options?.caption) {
        form.append("caption", options.caption);
    }
    await postTelegramMultipart(token, "sendPhoto", form);
}

/**
 * Sends a video via sendVideo (multipart).
 */
export async function sendTelegramVideo(
    botToken: string,
    chatId: string,
    file: Blob,
    options?: { caption?: string; filename?: string }
): Promise<void> {
    const { token, chat } = requireTokenChat(botToken, chatId);
    const form = new FormData();
    form.append("chat_id", chat);
    const name =
        options?.filename ||
        (file instanceof File ? file.name : null) ||
        "clip.mp4";
    form.append("video", file, name);
    if (options?.caption) {
        form.append("caption", options.caption);
    }
    await postTelegramMultipart(token, "sendVideo", form);
}

/**
 * Sends a file via sendDocument (multipart). Use for large images (e.g. maps); limit is higher than sendPhoto.
 */
export async function sendTelegramDocument(
    botToken: string,
    chatId: string,
    file: Blob,
    options?: { caption?: string; filename?: string }
): Promise<void> {
    const { token, chat } = requireTokenChat(botToken, chatId);
    const form = new FormData();
    form.append("chat_id", chat);
    const name =
        options?.filename ||
        (file instanceof File ? file.name : null) ||
        "file.bin";
    form.append("document", file, name);
    if (options?.caption) {
        form.append("caption", options.caption);
    }
    await postTelegramMultipart(token, "sendDocument", form);
}
